import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CompanyType } from 'src/app/interfaces/company-type';
import { Jurisdiction } from 'src/app/interfaces/jurisdiction';
import { StockExchange } from 'src/app/interfaces/stock-exchanges';

import { AddressForm } from './address-form';
import { CompanyVendorForm } from './company-vendor-form';

export class VendorForm extends FormGroup<{
  id: FormControl<undefined | number>;
  apiSource: FormControl<null | string>;
  apiKey: FormControl<null | string>;
  legalName: FormControl<undefined | string>;
  logo: FormControl<null | string>;
  dba: FormControl<null | string>;
  ein: FormControl<null | string>;
  ssn: FormControl<null | string>;
  bsn: FormControl<null | string>;
  rsn: FormControl<null | string>;
  usesSIN: FormControl<boolean>;
  isWorkersCompExempt: FormControl<boolean>;
  isPubliclyTraded: FormControl<undefined | boolean>;
  qtyEmployees: FormControl<undefined | number>;
  employsSubContractors: FormControl<boolean>;
  stockExchange: FormControl<null | StockExchange>;
  stockSymbol: FormControl<null | string>;
  yearEstablished: FormControl<null | number>;
  hasComplexOwnership: FormControl<boolean>;
  website: FormControl<null | string>;
  linkedin: FormControl<null | string>;
  facebook: FormControl<null | string>;
  companyType: FormControl<null | CompanyType>;
  jurisdiction: FormControl<null | Jurisdiction>;
  llcType: FormControl<'C' | 'S' | 'P' | 'disregardedEntity' | null>;
  country: FormControl<'US' | 'CA' | undefined>;
  phone: FormControl<null | string>;
  phoneExt: FormControl<null | string>;
  phone2: FormControl<null | string>;
  fax: FormControl<null | string>;
  serviceArea: FormControl<null | string>;
  invitedByCompanyId: FormControl<null | number>;
  isOnSite: FormControl<boolean>;
  isExempt: FormControl<boolean>;
  isProfessional: FormControl<boolean>;
  isSupplier: FormControl<boolean>;
  feesWaived: FormControl<boolean>;
  renewalDate: FormControl<null | Date>;
  vendorType: FormControl<null | string>;
  inviteCode: FormControl<null | string>;
  companyDescription: FormControl<null | string>;
  experienceDescription: FormControl<null | string>;
  tradeOrganizationsDescriptions: FormControl<null | string>;
  affiliationsDescription: FormControl<null | string>;
  syncronizePayments: FormControl<boolean>;
  allowDuplicateTaxId: FormControl<boolean>;
  isArchived: FormControl<boolean>;
  allowAfterHoursService: FormControl<boolean>;
  afterHoursPhone: FormControl<null | string>;
  afterHoursPhoneExt: FormControl<null | string>;

  Addresses: FormArray<AddressForm>;
  StreetAddress: AddressForm;
  MailingAddress: AddressForm;
  RemitToAddress: AddressForm;
  CompanyVendors?: FormArray<CompanyVendorForm>;
}> {
  constructor() {
    super({
      id: new FormControl<undefined | number>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      apiSource: new FormControl<null | string>(null),
      apiKey: new FormControl<null | string>(null),
      legalName: new FormControl<undefined | string>(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      logo: new FormControl<null | string>(null),
      dba: new FormControl<null | string>(null),
      ein: new FormControl<null | string>(null),
      ssn: new FormControl<null | string>(null),
      bsn: new FormControl<null | string>(null),
      rsn: new FormControl<null | string>(null),
      usesSIN: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      isWorkersCompExempt: new FormControl<boolean>(false, {
        nonNullable: true
      }),
      isPubliclyTraded: new FormControl<undefined | boolean>(false, {
        nonNullable: true
      }),
      qtyEmployees: new FormControl<undefined | number>(
        { value: undefined, disabled: false },
        { nonNullable: true }
      ),
      employsSubContractors: new FormControl<boolean>(false, {
        nonNullable: true
      }),
      stockExchange: new FormControl<null | StockExchange>(null),
      stockSymbol: new FormControl<null | string>(null),
      yearEstablished: new FormControl<null | number>(null, {
        validators: [Validators.required]
      }),
      hasComplexOwnership: new FormControl<boolean>(false, {
        nonNullable: true
      }),
      website: new FormControl<null | string>(null),
      linkedin: new FormControl<null | string>(null),
      facebook: new FormControl<null | string>(null),
      companyType: new FormControl<null | CompanyType>(null),
      jurisdiction: new FormControl<null | Jurisdiction>(null),
      llcType: new FormControl<'C' | 'S' | 'P' | 'disregardedEntity' | null>(
        null
      ),
      country: new FormControl<'US' | 'CA' | undefined>(
        { value: undefined, disabled: false },
        { nonNullable: true, validators: [Validators.required] }
      ),
      phone: new FormControl<null | string>(null),
      phoneExt: new FormControl<null | string>(null),
      phone2: new FormControl<null | string>(null),
      fax: new FormControl<null | string>(null),
      serviceArea: new FormControl<null | string>(null),
      invitedByCompanyId: new FormControl<null | number>(null),
      isOnSite: new FormControl<boolean>(false, { nonNullable: true }),
      isExempt: new FormControl<boolean>(false, { nonNullable: true }),
      isProfessional: new FormControl<boolean>(false, { nonNullable: true }),
      isSupplier: new FormControl<boolean>(false, { nonNullable: true }),
      feesWaived: new FormControl<boolean>(false, { nonNullable: true }),
      renewalDate: new FormControl<null | Date>(null),
      vendorType: new FormControl<null | string>(null),
      inviteCode: new FormControl<null | string>(null),
      companyDescription: new FormControl<null | string>(null),
      experienceDescription: new FormControl<null | string>(null),
      tradeOrganizationsDescriptions: new FormControl<null | string>(null),
      affiliationsDescription: new FormControl<null | string>(null),
      syncronizePayments: new FormControl<boolean>(false, {
        nonNullable: true
      }),
      allowDuplicateTaxId: new FormControl<boolean>(false, {
        nonNullable: true
      }),
      isArchived: new FormControl<boolean>(false, { nonNullable: true }),
      allowAfterHoursService: new FormControl<boolean>(false, {
        nonNullable: true
      }),
      afterHoursPhone: new FormControl<null | string>(null),
      afterHoursPhoneExt: new FormControl<null | string>(null),

      Addresses: new FormArray<AddressForm>([]),
      StreetAddress: new AddressForm(),
      MailingAddress: new AddressForm(),
      RemitToAddress: new AddressForm()
    });
  }
}

// address1?: string | null;
// address2?: string | null;
// city?: string | null;
// state?: string | null;
// zip?: string | null;
// latitude?: string | null;
// longitude?: string | null;
// tradeStates?: string | null;
// registrationStatus?: (
//  'created' | 'invited' | 'needpayment' | 'businessInfoComplete' | 'stepOnePartBComplete' |
//  'stepOnePartCComplete' | 'stepOneComplete' | 'stepTwoComplete' | 'stepThreeComplete' | 'stepFourComplete' |
//  'stepFiveComplete' | 'pendingPayment' | 'complete'
//)
// mailingAddress1?: string | null;
// mailingAddress2?: string | null;
// mailingCity?: string | null;
// mailingState?: string | null;
// mailingZip?: string | null;

/*
Owners?: Owner[];
Contacts?: Contact[];
ComplianceContact?: Contact;
BidContact?: Contact;
BillingContact?: Contact;
InsuranceAgents?: InsuranceAgent[];
InsuranceCertificates?: InsuranceCertificate[];
InsurancePolicies?: InsurancePolicy[];
VendorRankings?: VendorRanking[];
VendorTradeStates?: VendorTradeState[];
VendorTrades?: VendorTrade[];
VendorReviews?: VendorReview [];
RegistrationProgresses?: RegistrationProgress[];
SmallBusinessOptions?: Required<NonNullableInterface<Pick<SmallBusinessOption, 'id' | 'VendorId' | 'type'>>>[];
Notes?: Note[];
CompanyVendorPayments?: CompanyVendorPayment[];
PaymentIdentifiers?: PaymentIdentifier[];
Bundles?: Bundle[];
ChangeHistories?: ChangeHistory[];
VendorRanking?: VendorRanking;
ComplianceFinding?: ComplianceFinding | null;
VendorProfile?: VendorProfile;
Sites?: Site[] = [];
*/
